/**
 * Global tooltip directive with HTML support
 *
 * Usage:
 *   v-tooltip="'Tooltip text'"
 *   v-tooltip.top="'Tooltip text'"
 *   v-tooltip.bottom="'Tooltip text'"
 *   v-tooltip.html="'<strong>HTML</strong> content'"
 *   v-tooltip.top.html="'<strong>HTML</strong> content'"
 *
 * Examples:
 *   <button v-tooltip="'Click to save'">Save</button>
 *   <span v-tooltip.bottom="user.fullName">{{ user.shortName }}</span>
 *   <span v-tooltip.html="formatAccountTooltip(code)">{{ code }}</span>
 */

let tooltipElement = null;
let hideTimeout = null;
let activeTooltipEl = null;

// Hide the tooltip when tapping anywhere outside the element that owns it
// (touch devices have no mouseleave). Registered once, lazily.
let outsideTouchHandlerAttached = false;
function attachOutsideTouchHandler() {
  if (outsideTouchHandlerAttached) return;
  document.addEventListener('touchstart', (e) => {
    if (activeTooltipEl && !activeTooltipEl.contains(e.target)) {
      activeTooltipEl = null;
      hideTooltip();
    }
  }, { passive: true });
  outsideTouchHandlerAttached = true;
}

function createTooltipElement() {
  if (!tooltipElement) {
    tooltipElement = document.createElement('div');
    tooltipElement.className = 'custom-tooltip';
    tooltipElement.style.cssText = `
      position: fixed;
      z-index: 99999;
      background: #1a1a2e;
      color: #fff;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 0.8125rem;
      max-width: 320px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
      line-height: 1.4;
    `;
    document.body.appendChild(tooltipElement);
  }
  return tooltipElement;
}

function showTooltip(el, content, isHtml, position) {
  if (!content) return;

  clearTimeout(hideTimeout);

  const tooltip = createTooltipElement();

  if (isHtml) {
    tooltip.innerHTML = content;
  } else {
    tooltip.textContent = content;
  }

  tooltip.style.opacity = '1';

  // Position the tooltip
  const rect = el.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();

  let top, left;

  if (position === 'bottom') {
    top = rect.bottom + 8;
    left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
  } else {
    // Default to top
    top = rect.top - tooltipRect.height - 8;
    left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
  }

  // Keep tooltip within viewport
  if (top < 5) {
    top = rect.bottom + 8; // Flip to bottom
  }
  if (left < 5) {
    left = 5;
  }
  if (left + tooltipRect.width > window.innerWidth - 5) {
    left = window.innerWidth - tooltipRect.width - 5;
  }

  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
}

function hideTooltip() {
  hideTimeout = setTimeout(() => {
    if (tooltipElement) {
      tooltipElement.style.opacity = '0';
    }
  }, 100);
}

function setupTooltip(el, binding) {
  const isHtml = binding.modifiers.html;
  const position = binding.modifiers.bottom ? 'bottom' : 'top';

  el._tooltipContent = binding.value;
  el._tooltipIsHtml = isHtml;
  el._tooltipPosition = position;

  if (!el._tooltipHandlersAttached) {
    el._showTooltipHandler = () => {
      activeTooltipEl = el;
      showTooltip(el, el._tooltipContent, el._tooltipIsHtml, el._tooltipPosition);
    };
    el._hideTooltipHandler = () => {
      if (activeTooltipEl === el) activeTooltipEl = null;
      hideTooltip();
    };
    // Mouse click hides — but not the click synthesized right after the touch
    // that just opened the tooltip.
    el._clickTooltipHandler = () => {
      if (el._tooltipTouchShownAt && Date.now() - el._tooltipTouchShownAt < 600) return;
      el._hideTooltipHandler();
    };
    // Touch: tap toggles the tooltip (WCAG 1.4.13 — hover content must be
    // reachable without hover; touch devices have no hover).
    el._touchTooltipHandler = () => {
      attachOutsideTouchHandler();
      if (activeTooltipEl === el) {
        el._hideTooltipHandler();
      } else {
        el._tooltipTouchShownAt = Date.now();
        el._showTooltipHandler();
      }
    };

    el.addEventListener('mouseenter', el._showTooltipHandler);
    el.addEventListener('mouseleave', el._hideTooltipHandler);
    el.addEventListener('click', el._clickTooltipHandler);
    // Keyboard: focus shows, blur hides (was hover-only)
    el.addEventListener('focus', el._showTooltipHandler);
    el.addEventListener('blur', el._hideTooltipHandler);
    el.addEventListener('touchstart', el._touchTooltipHandler, { passive: true });

    el._tooltipHandlersAttached = true;
  }

  // Remove any existing title attribute to prevent browser's native tooltip
  el.removeAttribute('title');
}

function cleanupTooltip(el) {
  if (el._tooltipHandlersAttached) {
    el.removeEventListener('mouseenter', el._showTooltipHandler);
    el.removeEventListener('mouseleave', el._hideTooltipHandler);
    el.removeEventListener('click', el._clickTooltipHandler);
    el.removeEventListener('focus', el._showTooltipHandler);
    el.removeEventListener('blur', el._hideTooltipHandler);
    el.removeEventListener('touchstart', el._touchTooltipHandler);
    el._tooltipHandlersAttached = false;
  }
  if (activeTooltipEl === el) activeTooltipEl = null;
  el.removeAttribute('title');
  hideTooltip();
}

export const vTooltip = {
  mounted(el, binding) {
    if (binding.value) {
      setupTooltip(el, binding);
    }
  },

  updated(el, binding) {
    if (binding.value) {
      setupTooltip(el, binding);
    } else {
      cleanupTooltip(el);
    }
  },

  beforeUnmount(el) {
    cleanupTooltip(el);
  }
};

export default vTooltip;
